import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Settings, Users, Truck } from "lucide-react";
import { GenerateLoadingOrError } from "../../lib/utils";
import {
  useListOrganizations,
  useOrganizationMembers,
  OrganizationMember,
  OrganizationMemberPermission,
} from "../../lib/api/betterAuth";
import { useListCustomers } from "../../lib/api/customer";
import { DataTable } from "../../components/ui/DataTable/index";
import { Button } from "../../components/ui/button";
import { PermissionsModal } from "./components/PermissionsModal";
import { CreateUserModal } from "./components/CreateUserModal";
import { CustomerAssignmentModal } from "./components/CustomerAssignmentModal";
import { UpdateUserModal } from "./components/UpdateUserModal";

export function ClientOrgTab() {
  const navigate = useNavigate();
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
  const [showCustomerAssignmentModal, setShowCustomerAssignmentModal] = useState(false);
  const [showFreightAssignmentModal, setShowFreightAssignmentModal] = useState(false);
  const [selectedMemberForCustomers, setSelectedMemberForCustomers] = useState<OrganizationMember | null>(null);
  const [selectedMemberForFreight, setSelectedMemberForFreight] = useState<OrganizationMember | null>(null);
  const [selectedMemberForUpdate, setSelectedMemberForUpdate] = useState<OrganizationMember | null>(null);

  // Fetch client organizations
  const organizationsRes = useListOrganizations("client");

  // Get the first client organization (as mentioned in requirements)
  const clientOrg = organizationsRes?.data?.data?.[0];
  const clientOrgId = clientOrg?.id;

  // Fetch members for the client organization
  const membersRes = useOrganizationMembers(clientOrgId);

  // Fetch customers list with minimal flag
  const customersRes = useListCustomers({ minimal: true });
  const customers = customersRes?.data?.data || [];

  // Transform members data for DataTable - must be called before any conditional returns
  const members = membersRes?.data?.data || [];
  const tableData = useMemo(() => {
    return members.map((member: OrganizationMember) => {
      // Extract risk customer names from relations
      const riskRelations = (member.relations || []).filter(
        (rel) => rel.relation_type === "risk"
      );
      const riskProfileNames = riskRelations
        .map((rel) => rel.Customer?.long_name)
        .filter((name): name is string => !!name)
        .sort();

      // Extract freight customer names from relations
      const freightRelations = (member.relations || []).filter(
        (rel) => rel.relation_type === "freight"
      );
      const freightCustomerNames = freightRelations
        .map((rel) => [rel.Customer?.long_name, rel.is_implied ? "(Implied)" : undefined].filter(Boolean).join(" "))
        .filter((name): name is string => !!name)
        .sort();

      return {
        ...member,
        name: member.authuser?.name || "Unknown",
        riskProfiles: riskProfileNames.length > 0 ? riskProfileNames.join(", ") : "N/A",
        freightCustomers: freightCustomerNames.length > 0 ? freightCustomerNames.join(", ") : "N/A",
        phoneNumber: member.authuser?.phoneNumber || "No phone",
        displayRole: member.authuser?.user_id ? "Internal User" : member.role,
        permissionCount: (member.permissions || []).length,
        permissionsList: (member.permissions || [])
          .map((perm) => perm.permission_type.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()))
          .join(", ") || "None",
      };
    });
  }, [members]);

  const selectedMember = members.find((member: OrganizationMember) => member.id === selectedMemberId);
  const memberForCustomerAssignment = selectedMemberForCustomers ||
    (showCustomerAssignmentModal && selectedMemberId
      ? members.find((member: OrganizationMember) => member.id === selectedMemberId)
      : null);
  const memberForFreightAssignment = selectedMemberForFreight ||
    (showFreightAssignmentModal && selectedMemberId
      ? members.find((member: OrganizationMember) => member.id === selectedMemberId)
      : null);

  const loadingOrErrorUi = GenerateLoadingOrError([organizationsRes, membersRes, customersRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const columns = [
    {
      id: "name",
      accessorKey: "name",
      header: "User Name",
      minWidth: 150,
      maxWidth: 200,
      cell: (info: any) => (
        <button
          className="text-left hover:text-blue-600 font-medium"
          onClick={() => {
            const member = info.row.original as OrganizationMember & {
              name: string;
              phoneNumber: string;
            };
            handleRowClick(member);
          }}
        >
          {info.getValue() as string}
        </button>
      ),
    },
    {
      id: "riskProfiles",
      accessorKey: "riskProfiles",
      header: "Risk Profiles",
      minWidth: 200,
      maxWidth: 300,
      cell: (info: any) => {
        const value = info.getValue() as string;
        return (
          <span className="text-gray-600 text-sm">
            {value === "N/A" ? (
              <span className="text-gray-400 italic">No risk profiles</span>
            ) : (
              value
            )}
          </span>
        );
      },
    },
    {
      id: "freightCustomers",
      accessorKey: "freightCustomers",
      header: "Freight Customers",
      minWidth: 200,
      maxWidth: 300,
      cell: (info: any) => {
        const value = info.getValue() as string;
        return (
          <span className="text-gray-600 text-sm">
            {value === "N/A" ? (
              <span className="text-gray-400 italic">No freight customers</span>
            ) : (
              value
            )}
          </span>
        );
      },
    },
    {
      id: "phoneNumber",
      accessorKey: "phoneNumber",
      header: "Phone Number",
      minWidth: 150,
      maxWidth: 200,
      cell: (info: any) => (
        <span className="text-gray-600">{info.getValue() as string}</span>
      ),
    },
    {
      id: "role",
      accessorKey: "displayRole",
      header: "Role",
      minWidth: 120,
      maxWidth: 150,
      cell: (info: any) => (
        <span className="capitalize bg-gray-100 px-2 py-1 rounded-full text-xs">
          {info.getValue() as string}
        </span>
      ),
    },
    {
      id: "permissions",
      accessorKey: "permissions",
      accessorFn: (row: OrganizationMember) => {
        const permissions = row.permissions as OrganizationMemberPermission[];
        const formattedPermissions = permissions
          .map((perm) => perm.permission_type.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())).sort()
          .join(", ");
        return formattedPermissions;
      },
      header: "Permissions",
      minWidth: 250,
      cell: (info: any) => {
        const formattedPermissions = info.getValue();

        return (
          <span className="text-sm text-gray-600">
            {formattedPermissions || (
              <span className="text-gray-400 italic">No permissions</span>
            )}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      minWidth: 300,
      maxWidth: 300,
      enableFiltering: false,
      cell: (info: any) => {
        const member = info.row.original as OrganizationMember & {
          name: string;
          phoneNumber: string;
        };
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberForUpdate(member);
                setShowUpdateUserModal(true);
              }}
              title="Edit user"
              disabled={!member.authuser?.id}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberId(member.id);
                setShowPermissionsModal(true);
              }}
              title="Manage permissions"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberForCustomers(member);
                setShowCustomerAssignmentModal(true);
              }}
              title="Assign customers"
              disabled={!member.authuser?.id}
            >
              <Users className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberForFreight(member);
                setShowFreightAssignmentModal(true);
              }}
              title="Assign freight customer"
              disabled={!member.authuser?.id}
            >
              <Truck className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleRowClick = (member: OrganizationMember & { name: string; phoneNumber: string }) => {
    navigate(`/permissions/mobile/${member.id}`, {
      state: { organizationId: clientOrgId },
    });
  };

  if (!clientOrg) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <div className="text-center py-8">
          <p className="text-gray-500">No client organization found.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            Client Organization
          </h1>
          <p className="text-gray-600 mt-2">
            Manage Better Auth permissions for {clientOrg.name}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateUserModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create User
        </Button>
      </div>

      {tableData.length === 0 ? (
        <div className="border rounded-lg p-8 text-center">
          <p className="text-gray-500">
            {membersRes.isLoading
              ? "Loading members..."
              : "No members found. Add a member to get started."}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 h-[70vh]">
          <DataTable
            columns={columns as any}
            data={tableData}
            enableVirtualization
            virtualizationRowHeight={37}
            virtualizationOverscan={2}
            enableSorting
            enableMultiSort
            enableFiltering
            stickyHeaders
            enableGrouping
            enableColumnVisibility
            enableColumnResizing
            enableColumnPinning
            defaultSorting={[{ id: "name", desc: false }]}
            className="flex-1 flex flex-col min-h-0"
            tableClassName="max-h-[calc(100vh-280px)]"
          />
        </div>
      )}

      <PermissionsModal
        open={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setSelectedMemberId(null);
        }}
        member={selectedMember}
        organizationId={clientOrgId}
      />

      <CreateUserModal
        open={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        organizationId={clientOrgId}
      />

      <CustomerAssignmentModal
        relationType="risk"
        open={showCustomerAssignmentModal}
        onClose={() => {
          setShowCustomerAssignmentModal(false);
          setSelectedMemberForCustomers(null);
        }}
        member={memberForCustomerAssignment}
      />

      <CustomerAssignmentModal
        relationType="freight"
        open={showFreightAssignmentModal}
        onClose={() => {
          setShowFreightAssignmentModal(false);
          setSelectedMemberForFreight(null);
        }}
        member={memberForFreightAssignment}
        maxAllowed={1}
      />

      {(showUpdateUserModal && selectedMemberForUpdate) && (
        <UpdateUserModal
          open={showUpdateUserModal}
          onClose={() => {
            setShowUpdateUserModal(false);
            setSelectedMemberForUpdate(null);
          }}
          member={selectedMemberForUpdate}
        />
      )}
    </section>
  );
}

export default ClientOrgTab;
